"use client";

import GridEmptyState from "@/components/Grid/EmptyState";
import type { Analytics } from "@/types";
import dictionaryConfig from "@/../config/dictionary.config";
import React, { useEffect, useMemo, useState } from "react";

import useProtocolStats from "@/hooks/api/useProtocolStats";
import useProtocol from "@/hooks/api/useProtocol";
import ProtocolAnalytics from "@/components/Protocol/Analytics";
import { formatCurrencyCompact, isDuneEmbeddedLinkValid } from "@/utils";

import ProductChart from "@/components/Protocol/ProductChart";

export default function ProtocolInsightsPage({ params: { slug } }: { params: { slug: string } }) {
  const [protocolId, setProtocolId] = useState<string | undefined>(undefined);

  const { data: singleProtocol } = useProtocol(slug);
  const { data: protocolStats, isError, isLoading } = useProtocolStats(protocolId);

  const analytics = useMemo<Analytics[]>(() => {
    return (Array.isArray(singleProtocol?.analytics) && singleProtocol?.analytics.filter(item => isDuneEmbeddedLinkValid(item.chart_url))) || [];
  }, [singleProtocol]);

  useEffect(() => {
    if (singleProtocol) {
      setProtocolId(singleProtocol.id);
    }
  }, [singleProtocol]);

  const noData = useMemo(() => !isLoading && isError && analytics.length === 0, [isLoading, isError, analytics.length]);

  return noData ? (
    <GridEmptyState message="No insights yet" />
  ) : (
    <div className="pt-6">
      {protocolStats && (
        <div className="my-8 flex w-full flex-col px-6 min-[744px]:mt-0 lg:flex-row">
          <div className="flex flex-1 flex-col items-center justify-center bg-[#edd3f814] bg-opacity-[0.08] p-6 md:flex-row">
            <div className="border-[rgba(64, 64, 64, 0.08)] mx-6 flex w-full flex-col items-center justify-center border-b-[1px] pb-[3px] pt-2 font-mono  md:border-b-0">
              <p className="p-0 text-xs uppercase leading-3 ">Protocol TVL</p>
              <p className=" p-0 text-[32px] leading-[32px] md:text-5xl md:leading-[48px]">{formatCurrencyCompact(protocolStats.tvl)}</p>
            </div>
            <div className="border-[rgba(64, 64, 64, 0.08)] mx-6 flex w-full flex-col items-center justify-center border-b-[1px] pb-[3px] pt-2 font-mono  md:border-b-0">
              <p className="p-0 text-xs uppercase leading-3 ">Protocol TVL 24h Change</p>
              <span className=" p-0 text-[32px] leading-[32px] md:text-5xl md:leading-[48px]">
                {protocolStats.tvl_24h_change !== dictionaryConfig.statsDefaultValue ? `${protocolStats.tvl_24h_change.toFixed(4)}%` : "N/A"}
              </span>
            </div>
            <div className="border-[rgba(64, 64, 64, 0.08)] mx-6 flex w-full flex-col items-center justify-center border-b-[1px] pb-[3px] pt-2 font-mono md:border-b-0">
              <p className="p-0 text-xs uppercase leading-3 ">Total # vaults</p>
              <span className=" p-0 text-[32px] leading-[32px] md:text-5xl md:leading-[48px]">{Intl.NumberFormat().format(protocolStats.total_vaults)}</span>
            </div>
          </div>
        </div>
      )}

      <ProductChart protocolId={protocolId} />
      <ProtocolAnalytics analytics={analytics} />
    </div>
  );
}
